/**
 * Utility functions for converting SVG icons to image data URLs for PDF export
 */

import { QUESTION_TYPE_SVG_ICONS, CHECKLIST_TICK_SVG } from './question-type-constants';

/**
 * Convert SVG string to data URL
 */
function svgToDataUrl(svgString: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
}

/**
 * Convert SVG to PNG image data URL using canvas
 * Renders at high resolution (4x) for crisp display when scaled down
 */
async function svgToImage(svgString: string, displayWidth: number = 24, displayHeight: number = 24): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Render at 4x resolution for retina quality (prevents pixelation in html2canvas)
      const scale = 4;
      canvas.width = displayWidth * scale;
      canvas.height = displayHeight * scale;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // Draw at high resolution
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(dataURL);
      } else {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to get canvas context'));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG image'));
    };

    img.src = url;
  });
}

/**
 * Get question icon as data URL (PNG preferred, SVG fallback)
 * Icons are rendered at high resolution (48x48px) but displayed at 12x12px for crisp quality
 */
export async function getQuestionIconDataUrl(type: string): Promise<string | null> {
  const svgIcon = QUESTION_TYPE_SVG_ICONS[type];
  if (!svgIcon) {
    return null;
  }

  try {
    // Render at 4x resolution (48x48px) for retina quality, displayed at 12x12px
    return await svgToImage(svgIcon, 12, 12);
  } catch (error) {
    console.warn(`Failed to convert icon for type ${type}:`, error);
    return svgToDataUrl(svgIcon);
  }
}

/**
 * Get checklist tick icon as data URL
 * Icon is rendered at high resolution (40x40px) but displayed at 10x10px for crisp quality
 */
export async function getChecklistTickDataUrl(): Promise<string | null> {
  try {
    // Render at 4x resolution (40x40px) for retina quality, displayed at 10x10px
    return await svgToImage(CHECKLIST_TICK_SVG, 10, 10);
  } catch (error) {
    console.warn('Failed to convert checklist tick icon:', error);
    return svgToDataUrl(CHECKLIST_TICK_SVG);
  }
}

